import React from 'react';
import { Application } from '../../../../../entities/Application';
import { IconEntry } from '../IconEntry';
import { IconSpan } from '../../../../../components/IconSpan';
import { RedactedResumeService } from '../../../../../services/v1/applicant_tracking/RedactedResumeService';
import { RedactedEntry } from '../RedactedEntry';
import { isEmptyString } from '../../../../../utils/isEmptyString';
import { ApplicationDocument } from '../../../../../entities/applicant_tracking/ApplicationDocument';
import { Collapse } from '../../../../../components/Collapse';

interface PropTypes {
  application: Application;
  hideCV?: boolean;
  setIsLoading: (v: boolean) => void;
}

function IconLink(props: {
  redacted: boolean;
  icon: string;
  spanText: string;
  link: string;
  className?: string;
  fileName?: string;
  preview?: boolean;
}) {
  if (isEmptyString(props.link)) {
    return null;
  }

  return (
    <IconEntry
      redacted={props.redacted}
      icon={props.icon}
      label={props.spanText}
      className={props.className}
    >
      <a
        href={props.link}
        className='text-info'
        target='_blank'
        rel='noreferrer'
        download={!props.preview && !props.redacted && props.fileName}
      >
        <span>{props.spanText}</span>
      </a>
    </IconEntry>
  );
}

function CV(props: {
  hideCV: boolean;
  application: Application;
  setIsLoading: (v: boolean) => void;
}) {
  function handleClick() {
    props.setIsLoading(true);

    RedactedResumeService.show(props.application.applicationResume.id).then(
      (resume) => {
        window.open(resume.url, '_blank');
        props.setIsLoading(false);
      },
    );
  }

  if (props.hideCV) return null;
  if (!props.application.applicationResume) return null;

  if (!props.application.redacted) {
    return (
      <IconLink
        icon='bi-file-text'
        spanText='View CV'
        link={props.application.applicationResume.url}
        redacted={props.application.redacted}
        fileName={props.application.applicationResume.filename}
        preview={true}
      />
    );
  }

  return (
    <div className='d-flex text-info' role='button' onClick={handleClick}>
      <IconSpan
        icon={{ name: 'bi-file-lock2', className: 'text-blue-200 fs-4' }}
        spanText={'View CV'}
      />
    </div>
  );
}

function CoverLetter(props: { application: Application }) {
  if (!props.application.applicationCoverLetter) return null;

  if (!props.application.redacted) {
    return (
      <IconLink
        icon='bi-file-earmark-text'
        spanText='View Cover Letter'
        link={props.application.applicationCoverLetter?.url}
        redacted={props.application.redacted}
        fileName={props.application.applicationCoverLetter.filename}
        preview={true}
      />
    );
  }

  return <RedactedEntry label={'View Cover Letter'} />;
}

function CollapseDocuments(props: { documents: ApplicationDocument[] }) {
  return (
    <Collapse
      className='text-info'
      elements={props.documents.map((document, index) => (
        <IconLink
          key={index}
          icon='bi-paperclip'
          className={index !== 0 && 'mt-2h'}
          spanText={document.filename}
          link={document?.url}
          fileName={document.filename}
          redacted={false}
          preview={true}
        />
      ))}
      collapseClassName='ms-4'
    >
      <IconSpan
        spanText={`Other Documents (${props.documents.length})`}
        icon={{
          name: 'bi-paperclip',
          className: 'text-dark-200',
        }}
      />
    </Collapse>
  );
}

function Documents(props: { application: Application }) {
  if (!props.application.applicationDocuments) return null;

  const documents = props.application.applicationDocuments;
  const size = documents.length;

  if (!props.application.redacted) {
    return (
      <>
        {props.application.applicationDocuments.length === 1 ? (
          <IconLink
            icon='bi-paperclip'
            spanText={documents[0].filename}
            link={documents[0]?.url}
            fileName={documents[0].filename}
            redacted={false}
            preview={true}
          />
        ) : (
          <CollapseDocuments documents={documents} />
        )}
      </>
    );
  }

  return (
    <RedactedEntry label={`Other Documents ${size == 1 ? '' : `(${size})`}`} />
  );
}

export function PortfolioAndCVAndCoverLetter(props: PropTypes) {
  return (
    <div className='d-flex flex-column gap-2h'>
      <CV
        hideCV={props.hideCV}
        application={props.application}
        setIsLoading={props.setIsLoading}
      />
      <IconLink
        icon='bi-laptop'
        link={props.application.portfolioUrl}
        redacted={props.application.redacted}
        spanText='View Portfolio'
      />
      <CoverLetter application={props.application} />
      <Documents application={props.application} />
    </div>
  );
}
